import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fmtDate, fmtDateTime } from "@/lib/api";

// Bubble pinned above the date but kept fully on screen.
const Bubble = ({ anchor, text, testId }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: 8, top: 0, visibility: "hidden" });
  useLayoutEffect(() => {
    const a = anchor.current?.getBoundingClientRect();
    const w = ref.current?.offsetWidth || 0;
    if (!a) return;
    const left = Math.max(8, Math.min(a.left + a.width / 2 - w / 2, window.innerWidth - w - 8));
    setPos({ left, top: a.top - 6, visibility: "visible" });
  }, [anchor]);
  return createPortal(
    <span ref={ref} role="tooltip" style={{ ...pos, transform: "translateY(-100%)" }}
      className="fixed z-[100] max-w-[calc(100vw-16px)] rounded-md bg-foreground text-background px-2 py-1 text-xs font-medium shadow-lg whitespace-normal pointer-events-none"
      data-testid={testId ? `${testId}-bubble` : undefined}>
      {text}
    </span>,
    document.body,
  );
};

// Shows the date; hovering (mouse) or holding a finger on it (phone) reveals the exact date and time.
export const HoverTime = ({ iso, label = "", detail, children, className = "", testId }) => {
  const [held, setHeld] = useState(false);
  const timer = useRef(null);
  const anchor = useRef(null);
  const tip = detail || (iso ? `${label}${fmtDateTime(iso)}` : "");
  if (!tip) return <span className={className} data-testid={testId}>{children ?? fmtDate(iso)}</span>;
  const release = () => { clearTimeout(timer.current); timer.current = null; setHeld(false); };
  const press = (e) => {
    if (e.pointerType === "mouse") return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHeld(true), 400);
  };
  return (
    <span ref={anchor} title={tip} onPointerDown={press} onPointerUp={release} onPointerCancel={release} onPointerLeave={release}
      onContextMenu={(e) => { if (held || timer.current) e.preventDefault(); }}
      className={`cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2 select-none [-webkit-touch-callout:none] ${className}`}
      data-testid={testId}>
      {children ?? fmtDate(iso)}
      {held && <Bubble anchor={anchor} text={tip} testId={testId} />}
    </span>
  );
};
